import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  CASE_REPORT_DATA_URL,
  CHECK_CASE_REPORT_URL,
  GENERATE_CASE_REPORT_URL,
  GET_CASE_REPORT_AUDIT_TRAIL_URL,
  GET_CASE_REPORT_CONFIGURATION_URL,
  GET_CASE_REPORT_HISTORY_URL,
  GET_CASE_REPORT_LOOKUPS_URL,
  SAVE_CASE_REPORT_CONFIGURATION_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';

const parse_api_data = (response) => {
  const raw = response?.data ?? response;
  if (typeof raw === 'string') return JSON.parse(raw);
  return raw || {};
};

const safe_array = (value) => (Array.isArray(value) ? value : []);
const CASE_REPORT_CONFIGURATION_STORAGE_KEY = 'probaworkflows.caseReportConfiguration.v1';

const format_date_time = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default {
  name: 'DynamicCaseReport',
  mixins: [alert_mixin],
  data() {
    return {
      selected_project_id: false,
      lookups: {
        fields: [],
        dynamic_fields: [],
        client_types: [],
        case_statuses: [],
        assignees: [],
        chart_types: [],
        output_formats: [],
      },
      filters: {
        applicationtype: [],
        case_status: [],
        assigned: [],
        start_date: '',
        end_date: '',
      },
      selected_field_keys: [],
      field_search: '',
      dynamic_filters: [],
      output_format: 'xlsx',
      chart_type: '',
      freq: 'M',
      preview_rows: [],
      preview_fields: [],
      preview_count: 0,
      history: [],
      audit_trail: [],
      active_history_tab: 'history',
      loading_projects: false,
      loading_lookups: false,
      loading_history: false,
      previewing: false,
      checking: false,
      generating: false,
    };
  },
  computed: {
    route_project_id() {
      const project_id = Number(this.$route?.query?.project_id || this.$route?.query?.projectId || 0);
      return Number.isFinite(project_id) && project_id > 0 ? String(project_id) : '';
    },
    static_fields() {
      return safe_array(this.lookups.fields).map((field) => ({
        key: field.key,
        label: field.label || field.key,
        meta: 'Case field',
        dynamic: false,
      })).filter((field) => field.key);
    },
    dynamic_fields() {
      return safe_array(this.lookups.dynamic_fields).map((field) => ({
        key: field.key,
        section_question_id: field.section_question_id,
        label: field.label || field.key,
        meta: [field.form, field.section, field.question_type].filter(Boolean).join(' | '),
        question_type: field.question_type || '',
        dynamic: true,
      })).filter((field) => field.key);
    },
    all_fields() {
      return [...this.static_fields, ...this.dynamic_fields];
    },
    filtered_static_fields() {
      return this.filter_fields(this.static_fields);
    },
    filtered_dynamic_fields() {
      return this.filter_fields(this.dynamic_fields);
    },
    selected_fields() {
      const selected = new Set(this.selected_field_keys.map(String));
      return this.all_fields.filter((field) => selected.has(String(field.key)));
    },
    selected_static_count() {
      return this.selected_fields.filter((field) => !field.dynamic).length;
    },
    selected_dynamic_count() {
      return this.selected_fields.filter((field) => field.dynamic).length;
    },
    selected_field_count() {
      return this.selected_fields.length;
    },
    report_columns() {
      const keys = this.preview_fields.length ? this.preview_fields : this.selected_field_keys;
      return keys.map((key) => ({
        key,
        label: this.field_label(key),
      }));
    },
    visible_preview_rows() {
      return this.preview_rows.slice(0, 75);
    },
    export_formats() {
      const formats = safe_array(this.lookups.output_formats);
      const defaults = this.chart_type ? ['pdf', 'png'] : ['xlsx', 'csv', 'pdf', 'json'];
      const available = formats.length ? formats : defaults;
      return available.filter((format) => (this.chart_type ? ['pdf', 'png'].includes(format) : format !== 'png'));
    },
    active_history_rows() {
      return this.active_history_tab === 'audit' ? this.audit_trail : this.history;
    },
    has_project() {
      return true;
    },
  },
  watch: {
    selected_project_id() {
      this.handle_project_change();
    },
    chart_type(value) {
      if (value && !['pdf', 'png'].includes(this.output_format)) {
        this.output_format = 'pdf';
      }
      if (!value && this.output_format === 'png') {
        this.output_format = 'xlsx';
      }
    },
  },
  mounted() {
    this.bootstrap_page();
  },
  methods: {
    async bootstrap_page() {
      this.selected_project_id = this.route_project_id || false;
      await this.fetch_report_lookups();
      if (this.selected_project_id) {
        await this.fetch_report_history();
      }
    },
   extract_items(payload, preferred_key = 'data') {
  const parsed = parse_api_data(payload);

  const allowed_keys = {
    data: parsed?.data,
    results: parsed?.results,
    projects: parsed?.projects,
  };

  const preferred_items = allowed_keys[preferred_key] ?? null;

  const candidates = [
    preferred_items,
    parsed?.data,
    parsed?.results,
    parsed?.projects,
    parsed,
  ];

  return safe_array(
    candidates.find((candidate) => Array.isArray(candidate))
  );
},
    async handle_project_change() {
      this.preview_rows = [];
      this.preview_fields = [];
      this.preview_count = 0;
    },
    async fetch_report_lookups() {
      this.loading_lookups = true;
      try {
        const response = await axios.get(
          GET_CASE_REPORT_LOOKUPS_URL,
          get_request_config()
        );
        const parsed = parse_api_data(response);
        this.lookups = {
          ...this.lookups,
          ...(parsed.data || {}),
        };
        if (!(await this.restore_saved_configuration())) {
          this.apply_default_fields();
        }
        if (!this.export_formats.includes(this.output_format)) {
          this.output_format = this.export_formats[0] || 'xlsx';
        }
      } catch (error) {
        this.show_error(this.error_message(error, 'Failed to load report fields.'));
      } finally {
        this.loading_lookups = false;
      }
    },
    apply_default_fields() {
      const available_keys = new Set(this.dynamic_fields.map((field) => String(field.key)));
      const preserved = this.selected_field_keys.filter((key) => available_keys.has(String(key)));
      if (preserved.length) {
        this.selected_field_keys = preserved;
        return;
      }
      this.selected_field_keys = this.dynamic_fields.slice(0, 12).map((field) => field.key);
    },
    report_configuration_storage_key(project_id = false) {
      let organization_scope = 'global';
      try {
        const organization = JSON.parse(sessionStorage.getItem('current_organization') || 'null');
        if (organization?.id && organization.external_org_id !== 'ORG-LEGACY') {
          organization_scope = `organization.${organization.id}`;
        }
      } catch {
        // Treat malformed session context as the global scope.
      }
      return `${CASE_REPORT_CONFIGURATION_STORAGE_KEY}.${organization_scope}.${project_id || 'default'}`;
    },
    saved_dynamic_field_keys() {
      const dynamic_keys = new Set(this.dynamic_fields.map((field) => String(field.key)));
      return this.selected_field_keys.filter((key) => dynamic_keys.has(String(key)));
    },
    apply_saved_configuration(saved = {}) {
      const dynamic_keys = new Set(this.dynamic_fields.map((field) => String(field.key)));
      const selected_keys = safe_array(saved.selected_field_keys)
        .filter((key) => dynamic_keys.has(String(key)));
      this.selected_field_keys = selected_keys;
      return Boolean(selected_keys.length);
    },
    normalize_dynamic_filter_rows(rows = []) {
      const dynamic_keys = new Set(this.dynamic_fields.map((field) => String(field.key)));
      return safe_array(rows)
        .map((row) => ({
          id: row.id || Date.now() + Math.random(),
          field_key: row.field_key,
          operator: row.operator || 'contains',
          value: row.value || '',
        }))
        .filter((row) => dynamic_keys.has(String(row.field_key)));
    },
    async restore_saved_configuration() {
      try {
        const response = await axios.get(
          GET_CASE_REPORT_CONFIGURATION_URL,
          get_request_config()
        );
        const parsed = parse_api_data(response);
        const saved = parsed.configuration || parsed.data || {};
        if (saved && this.apply_saved_configuration(saved)) {
          return true;
        }
      } catch {
        // Fall back to browser storage for local/dev setups that do not have the endpoint yet.
      }
      try {
        const raw = window.localStorage.getItem(this.report_configuration_storage_key());
        if (!raw) return false;
        const saved = JSON.parse(raw);
        return this.apply_saved_configuration(saved);
      } catch {
        return false;
      }
    },
    cache_report_configuration(configuration) {
      try {
        window.localStorage.setItem(
          this.report_configuration_storage_key(),
          JSON.stringify(configuration)
        );
      } catch {
        // Local cache is best-effort only; the backend save is the source of truth.
      }
    },
    async save_report_configuration() {
      const configuration = {
        project_id: null,
        selected_field_keys: this.saved_dynamic_field_keys(),
        dynamic_filters: [],
      };
      try {
        const response = await axios.post(
          SAVE_CASE_REPORT_CONFIGURATION_URL,
          configuration,
          get_request_config()
        );
        const parsed = parse_api_data(response);
        this.cache_report_configuration(parsed.configuration || parsed.data || configuration);
        this.show_success('Report field configuration saved to the backend.');
      } catch (error) {
        this.cache_report_configuration({
          ...configuration,
          saved_at: new Date().toISOString(),
        });
        if (error?.response) {
          this.show_error(this.error_message(error, 'Failed to save report field configuration.'));
          return;
        }
        this.show_error('Failed to save report field configuration.');
      }
    },
    filter_fields(fields) {
      const search = String(this.field_search || '').trim().toLowerCase();
      if (!search) return fields;
      return fields.filter((field) => [field.label, field.meta, field.key].join(' ').toLowerCase().includes(search));
    },
    is_field_selected(key) {
      return this.selected_field_keys.map(String).includes(String(key));
    },
    toggle_field(key) {
      if (this.is_field_selected(key)) {
        this.selected_field_keys = this.selected_field_keys.filter((field_key) => String(field_key) !== String(key));
      } else {
        this.selected_field_keys = [...this.selected_field_keys, key];
      }
    },
    select_all_fields(fields) {
      const selected = new Set(this.selected_field_keys.map(String));
      fields.forEach((field) => selected.add(String(field.key)));
      this.selected_field_keys = Array.from(selected);
    },
    clear_field_selection() {
      this.selected_field_keys = [];
      this.preview_rows = [];
      this.preview_fields = [];
    },
    field_label(key) {
      const field = this.all_fields.find((item) => String(item.key) === String(key));
      return field?.label || key;
    },
    selected_dynamic_filter_field(row) {
      return this.dynamic_fields.find((field) => String(field.key) === String(row.field_key)) || null;
    },
    build_dynamic_filter_payload() {
      return this.dynamic_filters
        .map((row) => {
          const field = this.selected_dynamic_filter_field(row);
          const values = String(row.value || '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);
          if (!field || !values.length) return null;
          return {
            section_question_id: Number(field.section_question_id),
            operator: row.operator,
            values,
          };
        })
        .filter(Boolean);
    },
    compact_list(values) {
      return safe_array(values).map(String).filter(Boolean);
    },
    build_payload() {
      return {
        project_id: Number(this.selected_project_id),
        applicationtype: this.compact_list(this.filters.applicationtype),
        case_status: this.compact_list(this.filters.case_status),
        assigned: this.compact_list(this.filters.assigned),
        start_date: this.filters.start_date || null,
        end_date: this.filters.end_date || null,
        fields: this.selected_field_keys,
        dynamic_filters: this.build_dynamic_filter_payload(),
        output_format: this.output_format,
        chart: this.chart_type || null,
        freq: this.freq,
      };
    },
    validate_report_request() {
      if (!this.selected_project_id) {
        this.show_error('Please select a project.');
        return false;
      }
      if (!this.selected_field_keys.length) {
        this.show_error('Select at least one report field.');
        return false;
      }
      return true;
    },
    async preview_report() {
      if (!this.validate_report_request()) return;
      this.previewing = true;
      try {
        const response = await axios.post(CASE_REPORT_DATA_URL, this.build_payload(), get_request_config());
        const parsed = parse_api_data(response);
        this.preview_rows = safe_array(parsed.data);
        this.preview_fields = safe_array(parsed.fields);
        this.preview_count = Number(parsed.count || this.preview_rows.length || 0);
        this.show_success(`${this.preview_count} row(s) loaded.`);
      } catch (error) {
        this.preview_rows = [];
        this.preview_fields = [];
        this.preview_count = 0;
        this.show_error(this.error_message(error, 'No report data found for these filters.'));
      } finally {
        this.previewing = false;
      }
    },
    async check_report() {
      if (!this.validate_report_request()) return;
      this.checking = true;
      try {
        const response = await axios.post(CHECK_CASE_REPORT_URL, this.build_payload(), get_request_config());
        const parsed = parse_api_data(response);
        this.show_success(`${parsed.count || 0} matching case(s) found.`);
      } catch (error) {
        this.show_error(this.error_message(error, 'No report data found for these filters.'));
      } finally {
        this.checking = false;
      }
    },
    async generate_report() {
      if (!this.validate_report_request()) return;
      this.generating = true;
      const loader = await this.show_loader('Generating case report...');
      try {
        const response = await axios.post(GENERATE_CASE_REPORT_URL, this.build_payload(), get_request_config());
        const parsed = parse_api_data(response);
        const report = parsed.report || parsed.data || {};
        this.show_success('Case report generated.');
        this.open_report_url(report);
        await this.fetch_report_history();
      } catch (error) {
        this.show_error(this.error_message(error, 'Failed to generate case report.'));
      } finally {
        await this.close_loader(loader);
        this.generating = false;
      }
    },
    async fetch_report_history() {
      if (!this.selected_project_id) return;
      this.loading_history = true;
      const config = get_request_config({ params: { project_id: this.selected_project_id } });
      try {
        const [history_response, audit_response] = await Promise.all([
          axios.get(GET_CASE_REPORT_HISTORY_URL, config),
          axios.get(GET_CASE_REPORT_AUDIT_TRAIL_URL, config),
        ]);
        this.history = safe_array(parse_api_data(history_response).data);
        this.audit_trail = safe_array(parse_api_data(audit_response).data);
      } catch (error) {
        this.history = [];
        this.audit_trail = [];
        this.show_error(this.error_message(error, 'Failed to load report history.'));
      } finally {
        this.loading_history = false;
      }
    },
    open_report_url(report) {
      const url = report?.presigned_url || report?.file_url;
      if (!url) {
        this.show_info('The report was generated but no file URL was returned.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    report_history_title(report) {
      return report?.name || `Case Report ${report?.report_id || report?.id || ''}`.trim();
    },
    report_history_meta(report) {
      const pieces = [
        report?.downloaded_by || report?.generated_by || 'Generated report',
        format_date_time(report?.date || report?.date_completed || report?.date_created),
        `${report?.details?.row_count ?? '-'} row(s)`,
      ];
      return pieces.filter(Boolean).join(' | ');
    },
    output_label(format) {
      return String(format || '').toUpperCase();
    },
    row_value(row, key) {
      const value = row?.[key];
      if (value === null || value === undefined || value === '') return '-';
      return String(value);
    },
    format_date_time,
    error_message(error, fallback) {
      const data = error?.response?.data;
      if (!data) return fallback;
      if (typeof data === 'string') return data;
      if (data.message) return data.message;
      if (data.detail) return data.detail;
      const first_key = Object.keys(data)[0];
      const first_value = first_key ? data[first_key] : '';
      if (Array.isArray(first_value)) return first_value.join(', ');
      if (typeof first_value === 'string') return first_value;
      return fallback;
    },
  },
};
